import { CartProductCard } from '@/components';
import { colors } from '@/constants';
import { useCartTotals, useGlobals, useTables, useDiscountAmountFlex, useDiscountFlex } from '@/hooks';
import { CartItems, useCart } from '@/hooks/CartContext';
import { cleanStr, formatCurrency } from '@/shared';
import { useCalcPrice } from '@/talons';
import { CategoryProps, PriceProductProps, ProductProps } from '@/types';
import { HandlePrices } from '@/shared/calculatePrices';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as S from './styles';

interface CartProductItem {
  item: CartItems;
  category: CategoryProps | undefined;
  product: ProductProps | undefined;
}

interface Product extends ProductProps {
  quantity: number;
  packing: string;
  currentPrice: PriceProductProps;
  sku: number;
  uid: string;
  option_uid: string;
  category_id: number;
}

interface SummaryProps {
  items: CartProductItem[];
  cartTotalQuantity?: number;
  eventOrigin?: string;
  filteredItems?: Array<any>;
  refreshCart?: () => void;
  categoryMap?: Map<string, { productList: Product[]; quantity: number; category_id: number }>;
  addProductToCart: ({
    sku,
    quantity,
    option_uid,
    uid,
    category_id,
  }: {
    sku: number;
    quantity: number;
    option_uid: string;
    uid: string;
    category_id: number;
  }) => void;
  updateProductAmount: ({ uid, sku, quantity }: { uid: string; sku: number; quantity: number }) => void;
}

const Summary = (props: SummaryProps) => {
  const { tables } = useTables();
  const removedList = useRef<{ [key: string]: string }>({});
  const { dicionario_produtos: dicionarioProdutos } = tables.current;
  const { items, cartTotalQuantity, updateProductAmount, addProductToCart } = props;

  const { paymentCode } = useGlobals();
  const { findPrice } = useCalcPrice();
  const modePayment = useMemo(() => Number(paymentCode), [paymentCode]);
  const { cartTotals } = useCartTotals();
  const { currentCartDetails, filterTerm, getLimit, removeMultipleProductsFromCart, totalVolume } = useCart();
  const { discountAmountClient, increasesClient } = useDiscountAmountFlex();

  const findSku = useCallback(
    (sku: number) => {
      return currentCartDetails?.items?.find((it) => it.sku === sku);
    },
    [currentCartDetails]
  );

  const itemsFiltered = useMemo(() => {
    if (!items?.length) return [];

    return items
      ?.filter((i) => i?.product?.DESC_PRODUTO && cleanStr(i?.product?.DESC_PRODUTO)?.includes(cleanStr(filterTerm)))
      ?.map((item) => {
        const precoArr = item?.product?.PRECO;
        const hasPreco = Array.isArray(precoArr) && precoArr.length > 0;
        return {
          ...item?.item,
          ...item?.product,
          category: item?.category,
          packing: item?.item?.option_uid,
          currentPrice: hasPreco ? findPrice(precoArr, item?.item?.option_uid, modePayment) : ({} as PriceProductProps),
        };
      });
  }, [filterTerm, items, findPrice, modePayment]);

  // Verificação de itens problemáticos (sem embalagem/preço/limite/produto): roda no máximo
  // uma vez por sessão de carrinho. Antes, este efeito disparava a cada update do carrinho
  // (`removeMultipleProductsFromCart` muda de identidade quando `cartDetails` muda) e empilhava
  // timers concorrentes; ao final, podia chamar `window.location.reload()` em loop quando a
  // condição que causou a remoção persistia após o reload (ex.: dicionário ainda hidratando ou
  // catálogo da bandeira atual ainda vazio).
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    if (!itemsFiltered?.length) return;
    if (!dicionarioProdutos || !Object.keys(dicionarioProdutos).length) return;

    hasCheckedRef.current = true;

    const timeoutId = window.setTimeout(() => {
      const removed: Record<string, string> = {};
      itemsFiltered.forEach((i) => {
        const isEqualPack = (e: any, qtd: string) => `${e.SG_EMBALAGEM}_${e[qtd]}` === i.packing;
        const pckg = i?.EMBALAGEM?.find((e) => isEqualPack(e, 'QTD_EMBALAGEM') || isEqualPack(e, 'QTD_MULTIPLO_VENDA'));
        const hasPrice = !!i.PRECO?.length;
        const qtd = i.quantity * (pckg?.QTD_EMBALAGEM || 0);
        const hasLimit = !!qtd && qtd <= getLimit(i.sku);
        const hasProduct = !!dicionarioProdutos?.[i.sku];
        const produto = i.DESC_PRODUTO?.toUpperCase() || '';

        if (!!pckg && hasLimit && hasProduct && hasPrice) return;

        if (!pckg) removed[i.uid] = `Produto sem embalagem:\n${i.sku}-${produto}`;
        else if (!hasPrice) removed[i.uid] = `Produto sem preço:\n${i.sku}-${produto}`;
        else if (!hasLimit) removed[i.uid] = `Produto sem limite:\n${i.sku}-${produto}`;
        else if (!hasProduct) removed[i.uid] = `Produto não encontrado:\n${i.sku}-${produto}`;
      });

      const uids = Object.keys(removed);
      if (!uids.length) return;

      removedList.current = { ...removedList.current, ...removed };
      const fullMessage = Object.values(removed).join('\n\n');
      alert(fullMessage);
      removeMultipleProductsFromCart(uids);
      // Remoção já atualiza o estado do carrinho — não recarregamos a página para evitar
      // loop de reload quando o motivo da remoção persistir (ex.: catálogo vazio na bandeira).
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [itemsFiltered, dicionarioProdutos, getLimit, removeMultipleProductsFromCart]);

  const productsSubtotalFormatted = useMemo(() => formatCurrency({ value: cartTotals?.subtotal }), [cartTotals?.subtotal]);

  const priceSt = useMemo(() => formatCurrency({ value: cartTotals?.st_price }), [cartTotals?.st_price]);

  const totalSaldoAFaturar = useMemo(() => increasesClient || 0, [increasesClient]);

  const totalFlexComSaldo = useMemo(() => {
    const consumo = discountAmountClient || 0;
    const acrescimo = totalSaldoAFaturar || 0;
    return consumo + acrescimo;
  }, [discountAmountClient, totalSaldoAFaturar]);

  const formattedTotalFlexComSaldo = useMemo(() => formatCurrency({ value: totalFlexComSaldo }), [totalFlexComSaldo]);

  if (!items?.length) return null;

  return (
    <>
      <S.Container>
        {filterTerm &&
          itemsFiltered?.map((i, idx) => {
            if (findSku(i?.sku)) {
              return (
                <S.CardContainer key={`card-item-${idx}-${i?.category_id}`}>
                  <CartProductCard
                    key={`card-item-${idx}-${i?.category_id}`}
                    item={i}
                    addProductToCart={addProductToCart}
                    updateProductAmount={updateProductAmount}
                  />
                </S.CardContainer>
              );
            }
            return null;
          })}

        {itemsFiltered.map((i, idx) => {
          return (
            <CartProductCard
              item={i}
              addProductToCart={addProductToCart}
              updateProductAmount={updateProductAmount}
              key={`card-item-${idx}-${i?.category_id}`}
            />
          );
        })}

        <S.SubtotalContainer>
          <S.SubtotalContainerRow>
            <S.Subtotal>
              Subtotal ({cartTotalQuantity && cartTotalQuantity > 1 ? `${cartTotalQuantity} itens` : `${cartTotalQuantity} item`}{' '}
              e {totalVolume} {totalVolume > 1 ? 'volumes' : 'volume'}
              ):
            </S.Subtotal>
          </S.SubtotalContainerRow>
          <S.Separator />

          <S.TextRow $isfirst={true}>
            <S.RegularText>Subtotal em produtos: </S.RegularText>
            <S.RegularText>{productsSubtotalFormatted || 'R$ 0,00'}</S.RegularText>
          </S.TextRow>

          <S.TextRow>
            <S.RegularText>Substituição Tributária: </S.RegularText>
            <S.RegularText>{priceSt || 'R$ 0,00'}</S.RegularText>
          </S.TextRow>

          <S.TextRow>
            <S.RegularText>Total flex aplicado:</S.RegularText>
            <S.RegularText
              $bold
              color={totalFlexComSaldo >= 0 ? colors.green : colors.red}
              title={`Desconto (consumiu flex): ${formatCurrency({
                value: discountAmountClient || 0,
              })}\nSaldo a faturar (acréscimo): ${formatCurrency({
                value: totalSaldoAFaturar || 0,
              })}`}
            >
              {formattedTotalFlexComSaldo}
            </S.RegularText>
          </S.TextRow>

          <S.TextRow $islast={true}>
            <S.RegularText>Frete</S.RegularText>
            <S.RegularText $bold color={colors.green}>
              Grátis
            </S.RegularText>
          </S.TextRow>
        </S.SubtotalContainer>
      </S.Container>
    </>
  );
};

export default Summary;
